import { createSlice } from '@reduxjs/toolkit';
import {
    getLiveGuidelineProductTagsByLiveGuidelineProductIdAction,
    getLiveGuidelineProductTagsByLiveGuidelineProductIdsAction,
    createLiveGuidelineProductTagAction,
    deleteLiveGuidelineProductTagAction,
} from '../../actions/liveGuidelineProductTagActions';
import { Tag } from '../../actions/tagActions/types';

export interface LiveGuidelineProductTag {
    id: number;
    liveGuidelineProductId: number;
    tagId: number;
    tag: Tag;
}

export interface LiveGuidelineProductTagState {
    byLiveGuidelineProductId: Record<
        number,
        { pending: boolean; tags: LiveGuidelineProductTag[]; fetchedAt?: number }
    >;
    pending: boolean;
}

const initialState: LiveGuidelineProductTagState = {
    byLiveGuidelineProductId: {},
    pending: false,
};

export const liveGuidelineProductTagSlice = createSlice({
    name: 'liveGuidelineProductTags',
    initialState,
    reducers: {
        clearLiveGuidelineProductTagsForLiveGuidelineProduct: (state, action) => {
            delete state.byLiveGuidelineProductId[action.payload];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(
                getLiveGuidelineProductTagsByLiveGuidelineProductIdAction.pending,
                (state, action) => {
                    const liveGuidelineProductId = action.meta.arg;
                    state.byLiveGuidelineProductId[liveGuidelineProductId] = {
                        pending: true,
                        tags: [],
                    };
                },
            )
            .addCase(
                getLiveGuidelineProductTagsByLiveGuidelineProductIdAction.fulfilled,
                (state, action) => {
                    const { liveGuidelineProductId, tags } = action.payload;
                    state.byLiveGuidelineProductId[liveGuidelineProductId] = {
                        pending: false,
                        tags,
                        fetchedAt: Date.now(),
                    };
                },
            )
            .addCase(
                getLiveGuidelineProductTagsByLiveGuidelineProductIdAction.rejected,
                (state, action) => {
                    const liveGuidelineProductId = action.meta.arg;
                    state.byLiveGuidelineProductId[liveGuidelineProductId] = {
                        pending: false,
                        tags: [],
                    };
                },
            )
            .addCase(createLiveGuidelineProductTagAction.fulfilled, (state, action) => {
                const liveGuidelineProductId = action.payload?.liveGuidelineProductId;
                const tag = action.payload?.created;
                if (!liveGuidelineProductId || !tag) {
                    return;
                }

                if (state.byLiveGuidelineProductId[liveGuidelineProductId]) {
                    const entry = state.byLiveGuidelineProductId[liveGuidelineProductId];
                    const idx = entry.tags.findIndex(
                        (t) => t.tagId === tag.tagId || t.id === tag.id,
                    );
                    if (idx === -1) {
                        entry.tags.push(tag);
                    } else {
                        entry.tags[idx] = tag;
                    }
                } else {
                    state.byLiveGuidelineProductId[liveGuidelineProductId] = {
                        pending: false,
                        tags: [tag],
                    };
                }
            })
            .addCase(deleteLiveGuidelineProductTagAction.fulfilled, (state, action) => {
                const id = action.payload?.id;
                for (const key of Object.keys(state.byLiveGuidelineProductId)) {
                    const pid = Number.parseInt(key, 10);
                    const entry = state.byLiveGuidelineProductId[pid];
                    if (!entry) {
                        continue;
                    }
                    entry.tags = entry.tags.filter((t) => t.id !== id);
                }
            })
            .addCase(
                getLiveGuidelineProductTagsByLiveGuidelineProductIdsAction.fulfilled,
                (state, action) => {
                    const allTags: LiveGuidelineProductTag[] = action.payload ?? [];
                    const grouped: Record<number, LiveGuidelineProductTag[]> = {};
                    for (const tag of allTags) {
                        const pid = tag.liveGuidelineProductId;
                        if (!grouped[pid]) {
                            grouped[pid] = [];
                        }
                        grouped[pid].push(tag);
                    }
                    for (const [key, tags] of Object.entries(grouped)) {
                        state.byLiveGuidelineProductId[Number(key)] = {
                            pending: false,
                            tags,
                            fetchedAt: Date.now(),
                        };
                    }
                },
            );
    },
});

export const { clearLiveGuidelineProductTagsForLiveGuidelineProduct } =
    liveGuidelineProductTagSlice.actions;
export default liveGuidelineProductTagSlice.reducer;
